import { StoryPoint } from 'magical-trails';

const IMAGE_EXTS = 'png|jpg|jpeg|webp|gif|svg|avif';
const AV_EXTS = 'mp4|webm|m4v|mov|mp3|m4a|wav|ogg|oga|aac|flac';
const extRegex = (exts: string) => new RegExp(`\\.(${exts})(\\?.*)?$`, 'i');

const IMAGE_EXT_REGEX = extRegex(IMAGE_EXTS);
const AV_EXT_REGEX = extRegex(AV_EXTS);
const ASSET_EXT_REGEX = extRegex(`${IMAGE_EXTS}|${AV_EXTS}`);

const MEDIA_KEY_REGEX = /image|video|audio/;

// Only ever preload/prefetch over https. This is also the contract the service
// worker enforces on the SET_ALLOWED_ASSETS / PREFETCH_ASSETS payloads it
// receives from here, so an attacker-controlled asset URL cannot smuggle a
// http:/blob:/javascript: target into the trail cache.
export function isHttpsUrl(value: unknown): value is string {
  return typeof value === 'string' && /^https:\/\//i.test(value);
}

// Recursively walk a story point (its pages AND any nested objects/arrays,
// e.g. a snapChase page's `character.imageUrl`) collecting https media URLs.
// The previous version only scanned top-level page keys, so nested media never
// made it into the allowlist and could be evicted from the offline cache.
// `key` is the property name the value was found under, so a media-shaped key
// (image/video/audio) OR an asset file extension qualifies a string as media.
function collectFromValue(
  key: string,
  value: unknown,
  urls: Set<string>
): void {
  if (typeof value === 'string') {
    if (
      isHttpsUrl(value) &&
      (MEDIA_KEY_REGEX.test(key.toLowerCase()) || ASSET_EXT_REGEX.test(value))
    ) {
      urls.add(value);
    }
    return;
  }
  if (Array.isArray(value)) {
    // Array items inherit the containing key's context (e.g. an `images: []`).
    value.forEach((item) => collectFromValue(key, item, urls));
    return;
  }
  if (value && typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) =>
      collectFromValue(k, v, urls)
    );
  }
}

function collectAssetUrlsFromStoryPoints(storyPoints: StoryPoint[]): string[] {
  const urls = new Set<string>();
  storyPoints.forEach((storyPoint) => collectFromValue('', storyPoint, urls));
  return Array.from(urls);
}

export default async function preloadImages(storyPoints: StoryPoint[]) {
  const assetUrls = collectAssetUrlsFromStoryPoints(storyPoints);

  // Fire the DOM/network preloads FIRST and unconditionally. These populate the
  // browser + SW caches on their own, so a stalled/absent serviceWorker.ready
  // (see below) must never gate them. All URLs are already https-only.

  // Preload images via DOM to ensure immediate display
  assetUrls
    .filter((u) => IMAGE_EXT_REGEX.test(u))
    .forEach((url) => {
      const img = new Image();
      img.src = url;
    });

  // Trigger fetch for videos/audio to warm the SW cache without attaching to the DOM
  assetUrls
    .filter((u) => AV_EXT_REGEX.test(u))
    .forEach((url) => {
      try {
        fetch(url, { mode: 'cors' }).catch(() => undefined);
      } catch (_) {
        // ignore
      }
    });

  // THEN notify the service worker of the assets that should be retained. This
  // awaits registration readiness, so it must come after the eager preloads.
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      reg.active?.postMessage({ type: 'SET_ALLOWED_ASSETS', urls: assetUrls });
      // Ask SW to prefetch if desired. Preload via fetch above also populates cache.
      reg.active?.postMessage({ type: 'PREFETCH_ASSETS', urls: assetUrls });
    } catch (_) {
      // ignore
    }
  }
}
